/**
 * WHF Theme — Navigation interactivity.
 *
 * Handles:
 * - Drill-down sliding navigation (desktop + mobile unified)
 * - Search panel (drill-nav search mode)
 * - Sticky header scroll shadow
 * - Footer sitemap accordion (mobile)
 */

export function initNavigation() {
	initStickyHeader();
	initAlertTicker();
	initDrillNav();
	initFooterAccordion();
	initTranslateDropdown();
}

// ---------------------------------------------------------------------------
// Alert ticker — rotating notices beneath the nav, dismissible with memory
// ---------------------------------------------------------------------------
function initAlertTicker() {
	const ticker = document.querySelector( '[data-alert-ticker]' );
	if ( ! ticker ) return;

	const STORAGE_KEY = 'whfAlertDismissed';
	const signature = ticker.getAttribute( 'data-alert-signature' ) || '';

	// Respect a prior dismissal — but only if the alert content is unchanged.
	try {
		if ( window.localStorage.getItem( STORAGE_KEY ) === signature ) {
			return; // Stays hidden.
		}
	} catch ( e ) {
		// localStorage unavailable (private mode etc.) — just show the bar.
	}

	const items = Array.from( ticker.querySelectorAll( '[data-alert-item]' ) );
	if ( ! items.length ) return;

	// Reveal the bar and let the header re-measure its height (body padding
	// offset is driven by --whf-header-height from initStickyHeader).
	ticker.hidden = false;
	syncHeaderHeight();

	// Rotate through alerts when there's more than one.
	if ( items.length > 1 ) {
		const interval = parseInt( ticker.getAttribute( 'data-alert-interval' ), 10 ) || 6000;
		let index = 0;
		let timer = null;

		const advance = () => {
			items[ index ].classList.remove( 'is-active' );
			index = ( index + 1 ) % items.length;
			items[ index ].classList.add( 'is-active' );
		};

		const start = () => {
			timer = window.setInterval( advance, interval );
		};
		const stop = () => {
			if ( timer ) {
				window.clearInterval( timer );
				timer = null;
			}
		};

		start();
		// Pause on hover/focus so users can read / click.
		ticker.addEventListener( 'mouseenter', stop );
		ticker.addEventListener( 'mouseleave', start );
		ticker.addEventListener( 'focusin', stop );
		ticker.addEventListener( 'focusout', start );
	}

	// Dismiss.
	const closeBtn = ticker.querySelector( '[data-alert-dismiss]' );
	if ( closeBtn ) {
		closeBtn.addEventListener( 'click', () => {
			ticker.hidden = true;
			try {
				window.localStorage.setItem( STORAGE_KEY, signature );
			} catch ( e ) {
				// Ignore storage failures.
			}
			syncHeaderHeight();
		} );
	}
}

// Re-measure the header height into --whf-header-height after the alert
// bar shows/hides, so the body's top offset stays correct.
function syncHeaderHeight() {
	const header = document.querySelector( '[data-site-header]' );
	if ( ! header ) return;
	document.documentElement.style.setProperty(
		'--whf-header-height', header.offsetHeight + 'px'
	);
}

// ---------------------------------------------------------------------------
// Sticky header — add shadow class on scroll
// ---------------------------------------------------------------------------
function initStickyHeader() {
	const header = document.querySelector('[data-site-header]');
	if ( !header ) return;

	const updateHeaderHeight = () => {
		document.documentElement.style.setProperty(
			'--whf-header-height', header.offsetHeight + 'px'
		);
	};

	const onScroll = () => {
		header.classList.toggle( 'is-scrolled', window.scrollY > 10 );
		updateHeaderHeight();
	};

	window.addEventListener( 'scroll', onScroll, { passive: true } );
	window.addEventListener( 'resize', updateHeaderHeight, { passive: true } );
	onScroll();
}

// ---------------------------------------------------------------------------
// Drill Navigation — unified sliding panel for desktop + mobile
// ---------------------------------------------------------------------------
function initDrillNav() {
	const container = document.getElementById( 'drill-nav' );
	const dataEl = document.getElementById( 'whf-nav-data' );
	if ( !container || !dataEl ) return;

	let navData;
	try {
		navData = JSON.parse( dataEl.textContent );
	} catch ( e ) {
		return;
	}

	const { tree, promos, currentUrl } = navData;

	// State.
	const state = {
		isOpen: false,
		isSearchMode: false,
		path: [],        // stack of menu item IDs
		direction: 'forward',
	};

	// DOM references.
	const backdrop = container.querySelector( '.drill-nav__backdrop' );
	const panel = container.querySelector( '.drill-nav__panel' );
	const viewport = container.querySelector( '.drill-nav__viewport' );
	const breadcrumbsNav = container.querySelector( '.drill-nav__breadcrumbs' );
	const closeBtn = container.querySelector( '.drill-nav__close' );
	const promoArea = container.querySelector( '.drill-nav__promos' );
	const promoTrack = container.querySelector( '.drill-nav__promo-track' );
	const promoPrev = container.querySelector( '.drill-nav__promo-prev' );
	const promoNext = container.querySelector( '.drill-nav__promo-next' );
	const promoNav = container.querySelector( '.drill-nav__promo-nav' );
	const announcer = container.querySelector( '.drill-nav__announcer' );
	const triggers = document.querySelectorAll( '[data-drill-nav-trigger]' );
	const hamburger = document.querySelector( '[data-drill-nav-open]' );

	// Search DOM references.
	const searchPanel = container.querySelector( '.drill-nav__search' );
	const searchInput = container.querySelector( '.drill-nav__search-input' );
	const searchSubmitBtn = container.querySelector( '.drill-nav__search-submit' );
	const searchResultsPanel = container.querySelector( '.drill-nav__search-results' );
	const searchResultsScroll = container.querySelector( '.drill-nav__results-scroll' );
	const searchPills = container.querySelectorAll( '[data-search-term]' );
	const searchTriggers = document.querySelectorAll( '[data-search-trigger]' );

	let previousFocus = null;
	let promoIndex = 0;
	const mobileActions = panel.querySelector( '.drill-nav__mobile-actions' );
	const lgBreakpoint = 1024;

	// Search state.
	let searchDebounceTimer = null;
	let currentXhr = null;

	/**
	 * On mobile, move promos inside the panel (before mobile actions).
	 * On desktop, move them back to the container level.
	 */
	function repositionPromos() {
		if ( !promoArea ) return;
		const isMobile = window.innerWidth < lgBreakpoint;

		if ( isMobile && promoArea.parentNode !== panel ) {
			panel.insertBefore( promoArea, mobileActions );
		} else if ( !isMobile && promoArea.parentNode === panel ) {
			container.insertBefore( promoArea, container.querySelector( '.drill-nav__announcer' ) );
		}
	}

	window.addEventListener( 'resize', repositionPromos );
	repositionPromos();

	// -----------------------------------------------------------------------
	// Tree helpers
	// -----------------------------------------------------------------------

	/**
	 * Find a node in the tree by ID.
	 */
	function findNode( nodes, id ) {
		for ( const node of nodes ) {
			if ( node.id === id ) return node;
			if ( node.children && node.children.length ) {
				const found = findNode( node.children, id );
				if ( found ) return found;
			}
		}
		return null;
	}

	/**
	 * Find the path (array of IDs) to a URL in the tree.
	 * Returns the path to the deepest matching node, or null if not found.
	 */
	function findPathToUrl( nodes, url, trail ) {
		trail = trail || [];
		for ( const node of nodes ) {
			// Normalise URLs for comparison — trailing slash.
			const nodeUrl = node.url.replace( /\/?$/, '/' );
			const targetUrl = url.replace( /\/?$/, '/' );

			if ( nodeUrl === targetUrl ) {
				return [ ...trail, node.id ];
			}
			if ( node.children && node.children.length ) {
				const found = findPathToUrl( node.children, url, [ ...trail, node.id ] );
				if ( found ) return found;
			}
		}
		return null;
	}

	/**
	 * Work out the drill-nav state that opens directly to the level containing
	 * the current page: drill through every drillable ancestor, and if the page
	 * lives inside a final-level accordion, stop at the accordion's parent and
	 * flag the accordion to auto-expand. Highlights the active item either way.
	 *
	 * @param {?Array} autoPath IDs from a top item down to the active node.
	 * @return {{path: number[], autoExpandId: ?number, highlightUrl: ?string}}
	 */
	function computeAutoState( autoPath ) {
		if ( ! autoPath || ! autoPath.length ) {
			return { path: [], autoExpandId: null, highlightUrl: null };
		}

		const path = [ autoPath[0] ];
		let autoExpandId = null;

		// Walk the ancestors between the top item and the active node. Drill
		// into each one that still has drillable children; stop at the first
		// final-level accordion (the page is a link inside it) and expand that.
		for ( let i = 1; i < autoPath.length - 1; i++ ) {
			const node = findNode( tree, autoPath[i] );
			const drillable = node && node.children &&
				node.children.some( ( c ) => c.children && c.children.length );

			if ( drillable ) {
				path.push( autoPath[i] );
			} else {
				autoExpandId = autoPath[i];
				break;
			}
		}

		return { path, autoExpandId, highlightUrl: currentUrl };
	}

	/**
	 * Get items at the current path level.
	 */
	function getItemsAtPath( path ) {
		if ( path.length === 0 ) return tree;

		let items = tree;
		for ( const id of path ) {
			const node = items.find( ( n ) => n.id === id );
			if ( !node || !node.children ) return [];
			items = node.children;
		}
		return items;
	}

	/**
	 * Get the top-level slug for the current path.
	 */
	function getTopSlug( path ) {
		if ( path.length === 0 ) return null;
		const topNode = findNode( tree, path[0] );
		return topNode ? topNode.slug : null;
	}

	/**
	 * Get the slug for the current (deepest) level in the path.
	 * Falls back to the top-level slug.
	 */
	function getCurrentSlug( path ) {
		if ( path.length === 0 ) return null;
		const currentNode = findNode( tree, path[ path.length - 1 ] );
		return currentNode ? currentNode.slug : getTopSlug( path );
	}

	// -----------------------------------------------------------------------
	// Rendering
	// -----------------------------------------------------------------------

	/**
	 * Render a navigation level into the viewport.
	 */
	function renderLevel( items, direction, highlightUrl, autoExpandId ) {
		const newLevel = document.createElement( 'div' );
		newLevel.className = 'drill-nav__level';

		const ul = document.createElement( 'ul' );
		ul.className = 'drill-nav__list';

		items.forEach( ( item ) => {
			const li = document.createElement( 'li' );
			li.className = 'drill-nav__item';

			// Highlight current page.
			const itemUrl = item.url.replace( /\/?$/, '/' );
			const matchUrl = ( highlightUrl || '' ).replace( /\/?$/, '/' );
			if ( matchUrl && itemUrl === matchUrl ) {
				li.classList.add( 'is-active' );
			}

			const hasChildren = item.children && item.children.length > 0;
			// Final level: has children but none of them have children.
			const isFinalLevel = hasChildren && item.children.every(
				( c ) => !c.children || c.children.length === 0
			);

			if ( hasChildren && !isFinalLevel ) {
				// Drill item — arrow slides right on hover, drills deeper on click.
				const btn = document.createElement( 'button' );
				btn.className = 'drill-nav__link drill-nav__link--drill';
				btn.setAttribute( 'type', 'button' );
				btn.textContent = item.title;

				const chevron = document.createElement( 'span' );
				chevron.className = 'drill-nav__chevron';
				chevron.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="9 6 15 12 9 18"/></svg>';
				btn.appendChild( chevron );

				btn.addEventListener( 'click', () => {
					drillDown( item.id );
				} );

				li.appendChild( btn );

			} else if ( isFinalLevel ) {
				// Accordion item — arrow points down, flips up when expanded.
				const shouldAutoExpand = autoExpandId && item.id === autoExpandId;
				const btn = document.createElement( 'button' );
				btn.className = 'drill-nav__link drill-nav__link--accordion';
				btn.setAttribute( 'type', 'button' );
				btn.setAttribute( 'aria-expanded', shouldAutoExpand ? 'true' : 'false' );
				btn.textContent = item.title;

				const chevron = document.createElement( 'span' );
				chevron.className = 'drill-nav__chevron drill-nav__chevron--down';
				chevron.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>';
				btn.appendChild( chevron );

				// Build child list.
				const childUl = document.createElement( 'ul' );
				childUl.className = 'drill-nav__accordion-panel';
				item.children.forEach( ( child ) => {
					const childLi = document.createElement( 'li' );
					const childA = document.createElement( 'a' );
					childA.className = 'drill-nav__accordion-link';
					childA.href = child.url;
					childA.textContent = child.title;

					// Highlight current page inside accordion.
					const childUrl = child.url.replace( /\/?$/, '/' );
					if ( matchUrl && childUrl === matchUrl ) {
						childLi.classList.add( 'is-active' );
					}

					childLi.appendChild( childA );
					childUl.appendChild( childLi );
				} );

				btn.addEventListener( 'click', () => {
					const isExpanded = btn.getAttribute( 'aria-expanded' ) === 'true';
					if ( isExpanded ) {
						// Collapse: pin the current height, then animate to 0 next frame.
						childUl.style.maxHeight = childUl.scrollHeight + 'px';
						requestAnimationFrame( () => { childUl.style.maxHeight = '0px'; } );
						btn.setAttribute( 'aria-expanded', 'false' );
					} else {
						// Expand to the panel's real content height so nothing clips.
						btn.setAttribute( 'aria-expanded', 'true' );
						childUl.style.maxHeight = childUl.scrollHeight + 'px';
					}
				} );

				li.appendChild( btn );
				li.appendChild( childUl );

				// Auto-expanded on build (the active page lives inside) — set an
				// explicit height once laid out so a long list isn't clipped.
				if ( shouldAutoExpand ) {
					requestAnimationFrame( () => {
						childUl.style.maxHeight = childUl.scrollHeight + 'px';
					} );
				}

			} else {
				// Leaf link — underline on hover, goes directly to page.
				const a = document.createElement( 'a' );
				a.className = 'drill-nav__link drill-nav__link--leaf';
				a.href = item.url;
				a.textContent = item.title;
				li.appendChild( a );
			}

			ul.appendChild( li );
		} );

		newLevel.appendChild( ul );

		// Animate transition.
		const oldLevel = viewport.querySelector( '.drill-nav__level--active' );

		if ( oldLevel ) {
			const exitClass = direction === 'forward' ? 'drill-nav__level--exit-left' : 'drill-nav__level--exit-right';
			const enterClass = direction === 'forward' ? 'drill-nav__level--enter-right' : 'drill-nav__level--enter-left';

			newLevel.classList.add( enterClass );
			viewport.appendChild( newLevel );

			// Force reflow.
			newLevel.offsetHeight; // eslint-disable-line no-unused-expressions

			oldLevel.classList.remove( 'drill-nav__level--active' );
			oldLevel.classList.add( exitClass );
			newLevel.classList.remove( enterClass );
			newLevel.classList.add( 'drill-nav__level--active' );

			oldLevel.addEventListener( 'transitionend', () => {
				oldLevel.remove();
			}, { once: true } );

			// Fallback removal if transitionend doesn't fire.
			setTimeout( () => {
				if ( oldLevel.parentNode ) oldLevel.remove();
			}, 350 );
		} else {
			newLevel.classList.add( 'drill-nav__level--active' );
			viewport.appendChild( newLevel );
		}

		// Announce level change.
		if ( announcer ) {
			const pathLabels = state.path.map( ( id ) => {
				const node = findNode( tree, id );
				return node ? node.title : '';
			} ).filter( Boolean );
			announcer.textContent = pathLabels.length
				? 'Navigating: ' + pathLabels.join( ' > ' )
				: 'Navigation menu';
		}
	}

	/**
	 * Render breadcrumbs from the current path.
	 */
	function renderBreadcrumbs( path ) {
		breadcrumbsNav.innerHTML = '';

		if ( path.length === 0 ) {
			const span = document.createElement( 'span' );
			span.className = 'drill-nav__breadcrumb-item drill-nav__breadcrumb-item--active';
			span.textContent = 'Home';
			breadcrumbsNav.appendChild( span );
			return;
		}

		// "Menu" root link.
		const rootBtn = document.createElement( 'button' );
		rootBtn.className = 'drill-nav__breadcrumb-item';
		rootBtn.setAttribute( 'type', 'button' );
		rootBtn.textContent = 'Home';
		rootBtn.addEventListener( 'click', () => {
			drillTo( -1 );
		} );
		breadcrumbsNav.appendChild( rootBtn );

		path.forEach( ( id, index ) => {
			const sep = document.createElement( 'span' );
			sep.className = 'drill-nav__breadcrumb-sep';
			sep.setAttribute( 'aria-hidden', 'true' );
			sep.textContent = '\u203A';
			breadcrumbsNav.appendChild( sep );

			const node = findNode( tree, id );
			const label = node ? node.title : '';
			const isLast = index === path.length - 1;

			if ( isLast ) {
				const span = document.createElement( 'span' );
				span.className = 'drill-nav__breadcrumb-item drill-nav__breadcrumb-item--active';
				span.setAttribute( 'aria-current', 'step' );
				span.textContent = label;
				breadcrumbsNav.appendChild( span );
			} else {
				const btn = document.createElement( 'button' );
				btn.className = 'drill-nav__breadcrumb-item';
				btn.setAttribute( 'type', 'button' );
				btn.textContent = label;
				btn.addEventListener( 'click', () => {
					drillTo( index );
				} );
				breadcrumbsNav.appendChild( btn );
			}
		} );
	}

	/**
	 * Track current promo slug to avoid redundant re-renders.
	 */
	let currentPromoSlug = null;

	/**
	 * Render promo cards for the current section.
	 * When switching while already open, fades out old cards then slides in new ones.
	 */
	function renderPromos( slug ) {
		const cards = ( slug && promos[slug] ) ? promos[slug] : [];
		const isAlreadyOpen = container.classList.contains( 'is-animating' );
		const isSameSlug = slug === currentPromoSlug;

		// Skip if switching to the same promos.
		if ( isSameSlug && isAlreadyOpen ) return;

		currentPromoSlug = slug;

		if ( cards.length === 0 ) {
			if ( isAlreadyOpen && promoArea.classList.contains( 'is-visible' ) ) {
				// Fade out existing cards before hiding.
				promoArea.classList.add( 'is-switching-out' );
				setTimeout( () => {
					promoTrack.innerHTML = '';
					promoArea.classList.remove( 'is-visible', 'is-switching-out' );
				}, 250 );
			} else {
				promoTrack.innerHTML = '';
				promoArea.classList.remove( 'is-visible' );
			}
			return;
		}

		/**
		 * Build and insert new promo card elements.
		 */
		const insertCards = () => {
			promoTrack.innerHTML = '';
			promoIndex = 0;
			buildPromoCards( cards );

			promoArea.classList.add( 'is-visible' );

			// If already open, re-trigger the concertina by briefly removing is-animating.
			if ( isAlreadyOpen ) {
				container.classList.remove( 'is-animating' );
				// Force reflow so the browser registers the un-animated state.
				container.offsetHeight; // eslint-disable-line no-unused-expressions
				requestAnimationFrame( () => {
					container.classList.add( 'is-animating' );
				} );
			}
		};

		// If switching with cards already visible, fade out first.
		if ( isAlreadyOpen && promoArea.classList.contains( 'is-visible' ) && promoTrack.children.length ) {
			promoArea.classList.add( 'is-switching-out' );
			setTimeout( () => {
				promoArea.classList.remove( 'is-switching-out' );
				insertCards();
			}, 250 );
		} else {
			insertCards();
		}
	}

	/**
	 * Build promo card DOM elements and append to track.
	 */
	function buildPromoCards( cards ) {
		cards.forEach( ( card, index ) => {
			const el = document.createElement( 'a' );
			el.className = 'drill-nav__promo-card';
			if ( card.url ) {
				el.href = card.url;
			}
			if ( card.target ) {
				el.target = card.target;
				el.rel = 'noopener';
			}

			// Set stagger index as CSS custom property for concertina delay.
			el.style.setProperty( '--promo-index', index );

			let html = '';
			if ( card.image ) {
				html += '<div class="drill-nav__promo-card-image">';
				html += '<div class="drill-nav__promo-card-image-inner">';
				html += '<img src="' + card.image + '" alt="' + ( card.title || '' ).replace( /"/g, '&quot;' ) + '" loading="lazy">';
				html += '</div>';

				// Countdown timer — positioned over the bottom of the image.
				if ( card.countdown && card.date ) {
					html += '<div class="drill-nav__promo-countdown" data-countdown-target="' + escapeAttr( card.date ) + '" data-live-message="' + escapeAttr( card.liveMessage || 'Live' ) + '">';
					html += buildCountdownHtml( card.date );
					html += '</div>';
				}

				html += '</div>';
			} else if ( card.countdown && card.date ) {
				// No image — render countdown standalone.
				html += '<div class="drill-nav__promo-countdown drill-nav__promo-countdown--no-image" data-countdown-target="' + escapeAttr( card.date ) + '" data-live-message="' + escapeAttr( card.liveMessage || 'Live' ) + '">';
				html += buildCountdownHtml( card.date );
				html += '</div>';
			}

			html += '<div class="drill-nav__promo-card-body">';
			html += '<h3 class="drill-nav__promo-card-title">' + escapeHtml( card.title || '' ) + '</h3>';
			if ( card.date ) {
				const d = new Date( card.date.replace( ' ', 'T' ) );
				html += '<time class="drill-nav__promo-card-date" datetime="' + escapeAttr( card.date ) + '">';
				html += d.toLocaleDateString( undefined, { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit' } );
				html += '</time>';
			}
			if ( card.description ) {
				html += '<p class="drill-nav__promo-card-desc">' + escapeHtml( card.description ) + '</p>';
			} else if ( card.excerpt ) {
				html += '<p class="drill-nav__promo-card-desc">' + escapeHtml( card.excerpt ) + '</p>';
			}
			if ( card.buttonText && card.url ) {
				html += '<span class="drill-nav__promo-card-btn">' + escapeHtml( card.buttonText ) + '</span>';
			}
			html += '</div>';

			el.innerHTML = html;
			promoTrack.appendChild( el );
		} );

		// Set card count so the backdrop can delay after the last card.
		promoTrack.style.setProperty( '--promo-count', cards.length );

		// Show/hide carousel nav.
		if ( promoNav ) {
			promoNav.style.display = cards.length > 2 ? '' : 'none';
		}

		updatePromoPosition();
	}

	/**
	 * Update promo track position for carousel.
	 */
	function updatePromoPosition() {
		const cards = promoTrack.querySelectorAll( '.drill-nav__promo-card' );
		const maxIndex = Math.max( 0, cards.length - 2 );
		promoIndex = Math.max( 0, Math.min( promoIndex, maxIndex ) );

		promoTrack.style.transform = 'translateX(calc(-' + ( promoIndex * 50 ) + '%))';

		if ( promoPrev ) promoPrev.disabled = promoIndex === 0;
		if ( promoNext ) promoNext.disabled = promoIndex >= maxIndex;
	}

	// -----------------------------------------------------------------------
	// Search mode
	// -----------------------------------------------------------------------

	/**
	 * Tell other navs (e.g. flagship microsite mobile drill) we're opening
	 * so they can close themselves and avoid stacking overlays.
	 */
	function announceOpen() {
		document.dispatchEvent( new CustomEvent( 'whf:nav-open', { detail: { source: 'main' } } ) );
	}

	/**
	 * Open drill-nav in search mode.
	 */
	function openSearch() {
		announceOpen();
		// If nav is open in nav mode, switch to search mode.
		if ( state.isOpen && !state.isSearchMode ) {
			activateSearchMode();
			return;
		}

		// If already in search mode, just focus input.
		if ( state.isOpen && state.isSearchMode ) {
			if ( searchInput ) searchInput.focus();
			return;
		}

		// Open fresh in search mode.
		previousFocus = document.activeElement;
		state.isOpen = true;
		state.isSearchMode = true;
		state.path = [];

		container.classList.add( 'is-open', 'is-search-mode' );
		container.setAttribute( 'aria-hidden', 'false' );
		document.body.style.overflow = 'hidden';

		if ( hamburger ) hamburger.setAttribute( 'aria-expanded', 'true' );

		// Hide nav elements, show search elements.
		viewport.style.display = 'none';
		breadcrumbsNav.style.display = 'none';
		if ( mobileActions ) mobileActions.style.display = 'none';
		if ( searchPanel ) searchPanel.style.display = 'flex';
		if ( searchPanel ) searchPanel.setAttribute( 'aria-hidden', 'false' );

		// Results panel stays hidden until results arrive (controlled by .has-results class).
		if ( searchResultsPanel ) searchResultsPanel.classList.remove( 'has-results' );

		// Hide promos in search mode.
		promoArea.classList.remove( 'is-visible' );

		triggerOpenAnimation();

		// Focus search input after animation starts.
		setTimeout( () => {
			if ( searchInput ) searchInput.focus();
		}, 100 );
	}

	/**
	 * Switch from nav mode to search mode without closing.
	 */
	function activateSearchMode() {
		state.isSearchMode = true;
		state.path = [];

		container.classList.add( 'is-search-mode' );

		viewport.style.display = 'none';
		breadcrumbsNav.style.display = 'none';
		if ( mobileActions ) mobileActions.style.display = 'none';
		if ( searchPanel ) searchPanel.style.display = 'flex';
		if ( searchPanel ) searchPanel.setAttribute( 'aria-hidden', 'false' );

		// Results panel stays hidden until results arrive.
		if ( searchResultsPanel ) searchResultsPanel.classList.remove( 'has-results' );

		// Hide promos.
		promoArea.classList.remove( 'is-visible' );

		// Reset triggers.
		triggers.forEach( ( t ) => t.setAttribute( 'aria-expanded', 'false' ) );

		setTimeout( () => {
			if ( searchInput ) searchInput.focus();
		}, 100 );
	}

	/**
	 * Close search mode and restore nav mode elements.
	 */
	function closeSearch() {
		if ( !state.isSearchMode ) return;

		state.isSearchMode = false;
		container.classList.remove( 'is-search-mode' );

		// Clear search.
		if ( searchInput ) searchInput.value = '';
		if ( searchResultsScroll ) searchResultsScroll.innerHTML = '';
		if ( searchResultsPanel ) searchResultsPanel.classList.remove( 'has-results' );
		clearTimeout( searchDebounceTimer );
		if ( currentXhr ) {
			currentXhr.abort();
			currentXhr = null;
		}

		// Restore nav elements.
		viewport.style.display = '';
		breadcrumbsNav.style.display = '';
		if ( mobileActions ) mobileActions.style.display = '';
		if ( searchPanel ) searchPanel.style.display = '';
		if ( searchPanel ) searchPanel.setAttribute( 'aria-hidden', 'true' );
	}

	/**
	 * Perform debounced AJAX search.
	 */
	function performSearch( query ) {
		clearTimeout( searchDebounceTimer );
		if ( currentXhr ) {
			currentXhr.abort();
			currentXhr = null;
		}

		if ( !query || query.length < 2 ) {
			if ( searchResultsScroll ) searchResultsScroll.innerHTML = '';
			if ( searchResultsPanel ) searchResultsPanel.classList.remove( 'has-results' );
			return;
		}

		searchDebounceTimer = setTimeout( () => {
			// Show results panel with loading state.
			if ( searchResultsPanel ) searchResultsPanel.classList.add( 'has-results' );
			if ( searchResultsScroll ) {
				searchResultsScroll.innerHTML = '<div class="drill-nav__results-loading">Searching...</div>';
			}

			if ( typeof whfSearch === 'undefined' ) return;

			currentXhr = new XMLHttpRequest();
			currentXhr.open( 'GET', whfSearch.ajaxUrl + '?action=whf_live_search&s=' + encodeURIComponent( query ) );
			currentXhr.onload = function () {
				if ( currentXhr.status !== 200 ) return;

				let response;
				try {
					response = JSON.parse( currentXhr.responseText );
				} catch ( e ) {
					return;
				}

				if ( !response.success || !response.data.length ) {
					if ( searchResultsScroll ) {
						searchResultsScroll.innerHTML = '<div class="drill-nav__results-empty">No results found</div>';
					}
					return;
				}

				renderSearchResults( response.data, query );
			};
			currentXhr.send();
		}, 300 );
	}

	/**
	 * Render flat search results into the white panel.
	 */
	function renderSearchResults( results, query ) {
		if ( !searchResultsScroll ) return;

		let html = '';

		results.forEach( ( result, index ) => {
			html += '<a href="' + escapeAttr( result.url ) + '" class="drill-nav__result-card" style="--result-index:' + index + '">';
			if ( result.image ) {
				html += '<div class="drill-nav__result-image">';
				html += '<img src="' + escapeAttr( result.image ) + '" alt="" loading="lazy">';
				html += '</div>';
			}
			html += '<div class="drill-nav__result-content">';
			html += '<span class="drill-nav__result-type">' + escapeHtml( result.type ) + '</span>';
			html += '<span class="drill-nav__result-title">' + escapeHtml( result.title ) + '</span>';
			if ( result.excerpt ) {
				html += '<p class="drill-nav__result-excerpt">' + escapeHtml( result.excerpt ) + '</p>';
			}
			html += '<span class="drill-nav__result-cta">';
			html += 'Read More';
			html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>';
			html += '</span>';
			html += '</div>';
			html += '</a>';
		} );

		// View all results link.
		html += '<a href="/?s=' + encodeURIComponent( query ) + '" class="drill-nav__results-footer">';
		html += 'View all results';
		html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>';
		html += '</a>';

		searchResultsScroll.innerHTML = html;
	}

	// -----------------------------------------------------------------------
	// Navigation actions
	// -----------------------------------------------------------------------

	/**
	 * Trigger slide-in animations after display: flex is applied.
	 * Adds .is-animating on the next frame so CSS transitions fire.
	 */
	function triggerOpenAnimation() {
		// Force layout recalc so browser knows the start state.
		container.offsetHeight; // eslint-disable-line no-unused-expressions
		requestAnimationFrame( () => {
			container.classList.add( 'is-animating' );
		} );
	}

	/**
	 * Open the drill nav to a specific top-level menu item.
	 */
	function openToItem( menuItemId ) {
		announceOpen();
		// Close search mode if active.
		if ( state.isSearchMode ) {
			closeSearch();
		}

		previousFocus = document.activeElement;
		state.direction = 'forward';
		state.isOpen = true;

		// Try to find the current page within this section and open straight
		// to the level that contains it (falling back to the first child level).
		const topNode = findNode( tree, menuItemId );
		let autoPath = null;
		if ( currentUrl && topNode && topNode.children ) {
			autoPath = findPathToUrl( [ topNode ], currentUrl );
		}

		const auto = computeAutoState( autoPath );
		state.path = auto.path.length ? auto.path : [ menuItemId ];
		state.autoExpandId = auto.autoExpandId;
		state.highlightUrl = auto.highlightUrl;

		container.classList.add( 'is-open' );
		container.classList.remove( 'is-search-mode' );
		container.setAttribute( 'aria-hidden', 'false' );
		document.body.style.overflow = 'hidden';

		// Update all triggers.
		triggers.forEach( ( t ) => {
			t.setAttribute( 'aria-expanded',
				parseInt( t.getAttribute( 'data-drill-nav-trigger' ), 10 ) === menuItemId ? 'true' : 'false'
			);
		} );
		if ( hamburger ) hamburger.setAttribute( 'aria-expanded', 'true' );

		const items = getItemsAtPath( state.path );
		renderLevel( items, 'forward', state.highlightUrl, state.autoExpandId );
		renderBreadcrumbs( state.path );
		renderPromos( getCurrentSlug( state.path ) );
		triggerOpenAnimation();
		startCountdownTick();
	}

	/**
	 * Open to root level (hamburger / mobile).
	 */
	function openToRoot() {
		announceOpen();
		// Close search mode if active.
		if ( state.isSearchMode ) {
			closeSearch();
		}

		previousFocus = document.activeElement;
		state.direction = 'forward';
		state.isOpen = true;

		// Try to find the current page in the tree and open to its section,
		// drilling down to the level that contains it.
		let autoPath = null;
		if ( currentUrl ) {
			autoPath = findPathToUrl( tree, currentUrl );
		}

		const auto = computeAutoState( autoPath );
		state.path = auto.path;
		state.autoExpandId = auto.autoExpandId;
		state.highlightUrl = auto.highlightUrl;

		container.classList.add( 'is-open' );
		container.classList.remove( 'is-search-mode' );
		container.setAttribute( 'aria-hidden', 'false' );
		document.body.style.overflow = 'hidden';

		if ( hamburger ) hamburger.setAttribute( 'aria-expanded', 'true' );

		const items = getItemsAtPath( state.path );
		renderLevel( items, 'forward', state.highlightUrl, state.autoExpandId );
		renderBreadcrumbs( state.path );
		renderPromos( getCurrentSlug( state.path ) );
		triggerOpenAnimation();
		startCountdownTick();
	}

	/**
	 * Drill down into a child item.
	 */
	function drillDown( menuItemId ) {
		state.path.push( menuItemId );
		state.direction = 'forward';

		const items = getItemsAtPath( state.path );
		renderLevel( items, 'forward' );
		renderBreadcrumbs( state.path );
		renderPromos( getCurrentSlug( state.path ) );
	}

	/**
	 * Drill back to a specific path index.
	 * Pass -1 to return to root.
	 */
	function drillTo( toIndex ) {
		state.direction = 'backward';

		if ( toIndex < 0 ) {
			state.path = [];
		} else {
			state.path = state.path.slice( 0, toIndex + 1 );
		}

		const items = getItemsAtPath( state.path );
		renderLevel( items, 'backward' );
		renderBreadcrumbs( state.path );
		renderPromos( getCurrentSlug( state.path ) );
	}

	/**
	 * Close the drill nav with staged reverse animation.
	 * 1. Promos slide left (is-closing added)
	 * 2. Short delay, then panel slides left (is-animating removed)
	 * 3. Clean up after panel transition ends
	 */
	function close() {
		state.isOpen = false;
		state.path = [];
		stopCountdownTick();

		// Close search mode if active.
		closeSearch();

		// Reset triggers immediately.
		triggers.forEach( ( t ) => t.setAttribute( 'aria-expanded', 'false' ) );
		if ( hamburger ) hamburger.setAttribute( 'aria-expanded', 'false' );

		// Stage 1: promos slide left.
		container.classList.add( 'is-closing' );

		// Stage 2: after promos have started sliding, panel slides out.
		setTimeout( () => {
			container.classList.remove( 'is-animating' );

			// Stage 3: clean up after panel finishes.
			const onDone = () => {
				container.classList.remove( 'is-open', 'is-closing', 'is-search-mode' );
				container.setAttribute( 'aria-hidden', 'true' );
				document.body.style.overflow = '';
				promoArea.classList.remove( 'is-visible' );
				viewport.innerHTML = '';

				if ( previousFocus ) {
					previousFocus.focus();
					previousFocus = null;
				}
			};

			const handler = ( e ) => {
				if ( e.target === panel ) {
					panel.removeEventListener( 'transitionend', handler );
					onDone();
				}
			};
			panel.addEventListener( 'transitionend', handler );
			setTimeout( () => {
				panel.removeEventListener( 'transitionend', handler );
				if ( container.classList.contains( 'is-open' ) ) {
					onDone();
				}
			}, 400 );
		}, 150 );
	}

	// -----------------------------------------------------------------------
	// Event listeners
	// -----------------------------------------------------------------------

	// Close the drill-nav when another nav announces it's opening (e.g. the
	// flagship microsite mobile drill). Avoids stacked overlays.
	document.addEventListener( 'whf:nav-open', ( e ) => {
		if ( e.detail && 'main' !== e.detail.source && state.isOpen ) {
			close();
		}
	} );

	/**
	 * Switch the open drill-nav to a different top-level section, resetting to
	 * that section's immediate sub-menus. Unlike openToItem(), this does NOT
	 * auto-drill to the current page's location — clicking a sibling top-level
	 * item always lands on its first level.
	 *
	 * @param {number} id Top-level menu item ID.
	 */
	function switchToSection( id ) {
		state.path = [ id ];
		state.direction = 'forward';
		state.autoExpandId = null;
		state.highlightUrl = null;

		const items = getItemsAtPath( state.path );
		renderLevel( items, 'forward' );
		renderBreadcrumbs( state.path );
		renderPromos( getCurrentSlug( state.path ) );
		triggers.forEach( ( t ) => {
			t.setAttribute( 'aria-expanded',
				parseInt( t.getAttribute( 'data-drill-nav-trigger' ), 10 ) === id ? 'true' : 'false'
			);
		} );
	}

	// Desktop top-level nav triggers.
	triggers.forEach( ( trigger ) => {
		trigger.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			const id = parseInt( trigger.getAttribute( 'data-drill-nav-trigger' ), 10 );

			// If in search mode, switch to nav mode for this item.
			if ( state.isOpen && state.isSearchMode ) {
				closeSearch();
				switchToSection( id );
				return;
			}

			if ( state.isOpen && state.path[0] === id ) {
				close();
			} else if ( state.isOpen ) {
				// Switching to a different top-level section: reset to that
				// section's immediate sub-menus (don't auto-drill to the
				// current page's location within it).
				switchToSection( id );
			} else {
				openToItem( id );
			}
		} );
	} );

	// Search triggers — open search mode.
	searchTriggers.forEach( ( btn ) => {
		btn.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			e.stopPropagation();
			openSearch();
		} );
	} );

	// Search input — live search.
	if ( searchInput ) {
		searchInput.addEventListener( 'input', () => {
			const query = searchInput.value.trim();

			if ( query.length > 0 && query.length < 2 ) {
				// Show the panel with a hint to keep typing.
				if ( searchResultsPanel ) searchResultsPanel.classList.add( 'has-results' );
				if ( searchResultsScroll ) {
					searchResultsScroll.innerHTML = '<div class="drill-nav__results-hint">Keep typing to search...</div>';
				}
				return;
			}

			performSearch( query );
		} );

		// Enter key submits to search results page.
		searchInput.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Enter' ) {
				e.preventDefault();
				const query = searchInput.value.trim();
				if ( query.length >= 2 ) {
					window.location.href = '/?s=' + encodeURIComponent( query );
				}
			}
		} );
	}

	// Search submit button.
	if ( searchSubmitBtn && searchInput ) {
		searchSubmitBtn.addEventListener( 'click', () => {
			const query = searchInput.value.trim();
			if ( query.length >= 2 ) {
				window.location.href = '/?s=' + encodeURIComponent( query );
			}
		} );
	}

	// Popular search pills — populate input and trigger search.
	searchPills.forEach( ( pill ) => {
		pill.addEventListener( 'click', () => {
			const term = pill.getAttribute( 'data-search-term' );
			if ( searchInput && term ) {
				searchInput.value = term;
				searchInput.focus();
				performSearch( term );
			}
		} );
	} );

	// Hamburger — opens root level.
	if ( hamburger ) {
		hamburger.addEventListener( 'click', () => {
			if ( state.isOpen ) {
				close();
			} else {
				openToRoot();
			}
		} );
	}

	// Close button.
	if ( closeBtn ) {
		closeBtn.addEventListener( 'click', close );
	}

	// Backdrop click closes nav.
	if ( backdrop ) {
		backdrop.addEventListener( 'click', close );
	}

	// Promo area background click closes nav (clicks between/around cards).
	if ( promoArea ) {
		promoArea.addEventListener( 'click', ( e ) => {
			if ( e.target === promoArea || e.target === promoTrack ) {
				close();
			}
		} );
	}

	// Escape key.
	document.addEventListener( 'keydown', ( e ) => {
		if ( e.key === 'Escape' && state.isOpen ) {
			close();
		}
	} );

	// Focus trap.
	container.addEventListener( 'keydown', ( e ) => {
		if ( e.key !== 'Tab' || !state.isOpen ) return;

		const focusable = container.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);
		if ( !focusable.length ) return;

		const first = focusable[0];
		const last = focusable[focusable.length - 1];

		if ( e.shiftKey ) {
			if ( document.activeElement === first ) {
				e.preventDefault();
				last.focus();
			}
		} else {
			if ( document.activeElement === last ) {
				e.preventDefault();
				first.focus();
			}
		}
	} );

	// Promo carousel prev/next.
	if ( promoPrev ) {
		promoPrev.addEventListener( 'click', () => {
			promoIndex--;
			updatePromoPosition();
		} );
	}
	if ( promoNext ) {
		promoNext.addEventListener( 'click', () => {
			promoIndex++;
			updatePromoPosition();
		} );
	}

	// Promo touch swipe on mobile.
	let touchStartX = 0;
	if ( promoTrack ) {
		promoTrack.addEventListener( 'touchstart', ( e ) => {
			touchStartX = e.touches[0].clientX;
		}, { passive: true } );

		promoTrack.addEventListener( 'touchend', ( e ) => {
			const delta = touchStartX - e.changedTouches[0].clientX;
			if ( Math.abs( delta ) > 50 ) {
				if ( delta > 0 ) {
					promoIndex++;
				} else {
					promoIndex--;
				}
				updatePromoPosition();
			}
		}, { passive: true } );
	}
}

// ---------------------------------------------------------------------------
// Footer sitemap accordion — mobile toggle for section columns
// ---------------------------------------------------------------------------
function initFooterAccordion() {
	const toggles = document.querySelectorAll( '[data-footer-toggle]' );
	if ( !toggles.length ) return;

	toggles.forEach( ( toggle ) => {
		toggle.addEventListener( 'click', () => {
			const expanded = toggle.getAttribute( 'aria-expanded' ) === 'true';

			// Close all other sections.
			toggles.forEach( ( other ) => {
				if ( other !== toggle ) {
					other.setAttribute( 'aria-expanded', 'false' );
				}
			} );

			// Toggle this section.
			toggle.setAttribute( 'aria-expanded', expanded ? 'false' : 'true' );
		} );
	} );
}

// ---------------------------------------------------------------------------
// Countdown timer — ticks every second while nav is open
// ---------------------------------------------------------------------------
let countdownInterval = null;
const confettiCanvases = new Map();

function startCountdownTick() {
	if ( countdownInterval ) return;
	countdownInterval = setInterval( tickCountdowns, 1000 );
}

function stopCountdownTick() {
	if ( countdownInterval ) {
		clearInterval( countdownInterval );
		countdownInterval = null;
	}
	confettiCanvases.forEach( ( data ) => {
		if ( data.animId ) cancelAnimationFrame( data.animId );
		if ( data.canvas && data.canvas.parentNode ) data.canvas.parentNode.removeChild( data.canvas );
	} );
	confettiCanvases.clear();
}

function tickCountdowns() {
	const els = document.querySelectorAll( '[data-countdown-target]' );
	els.forEach( ( el ) => {
		const dateStr = el.getAttribute( 'data-countdown-target' );
		const liveMsg = el.getAttribute( 'data-live-message' ) || 'Live';
		const target = new Date( dateStr.replace( ' ', 'T' ) );
		const now = new Date();
		const diff = target - now;

		if ( diff <= 0 ) {
			if ( !el.classList.contains( 'is-live' ) ) {
				el.classList.add( 'is-live' );
				el.classList.remove( 'drill-nav__promo-countdown--urgent' );
				burstConfetti( el );
				setTimeout( () => {
					el.innerHTML = '<div class="drill-nav__promo-live"><span class="drill-nav__live-badge">' + escapeHtml( liveMsg ) + '</span></div>';
				}, 1500 );
			}
			return;
		}

		const oneHour = 60 * 60 * 1000;
		if ( diff <= oneHour && !el.classList.contains( 'drill-nav__promo-countdown--urgent' ) ) {
			el.classList.add( 'drill-nav__promo-countdown--urgent' );
			startGentleConfetti( el );
		}

		el.innerHTML = buildCountdownHtml( diff );
	} );
}

function buildCountdownHtml( diff ) {
	const days = Math.floor( diff / ( 1000 * 60 * 60 * 24 ) );
	const hours = Math.floor( ( diff % ( 1000 * 60 * 60 * 24 ) ) / ( 1000 * 60 * 60 ) );
	const mins = Math.floor( ( diff % ( 1000 * 60 * 60 ) ) / ( 1000 * 60 ) );
	const secs = Math.floor( ( diff % ( 1000 * 60 ) ) / 1000 );

	const tile = ( val, label ) =>
		'<span class="drill-nav__countdown-tile">' +
		'<span class="drill-nav__countdown-value">' + val + '</span>' +
		'<span class="drill-nav__countdown-label">' + label + '</span>' +
		'</span>';

	let html = '';
	if ( days > 0 ) html += tile( days, 'days' );
	html += tile( padZero( hours ), 'hrs' );
	html += tile( padZero( mins ), 'min' );
	html += tile( padZero( secs ), 'sec' );
	return html;
}

function padZero( n ) {
	return n < 10 ? '0' + n : '' + n;
}

// ---------------------------------------------------------------------------
// Confetti
// ---------------------------------------------------------------------------
const CONFETTI_COLORS = [ '#d61b26', '#1b7f77', '#f8dbc2', '#ffffff', '#e7767c', '#76b2ad' ];

function startGentleConfetti( container ) {
	if ( confettiCanvases.has( container ) ) return;

	const canvas = document.createElement( 'canvas' );
	canvas.className = 'drill-nav__confetti';
	container.style.position = 'relative';
	container.appendChild( canvas );

	const ctx = canvas.getContext( '2d' );
	const particles = [];
	canvas.width = container.offsetWidth;
	canvas.height = container.offsetHeight;

	let animId;
	const draw = () => {
		ctx.clearRect( 0, 0, canvas.width, canvas.height );
		if ( Math.random() < 0.12 ) {
			particles.push( {
				x: Math.random() * canvas.width,
				y: -4,
				s: Math.random() * 3 + 1.5,
				vy: Math.random() * 0.4 + 0.2,
				vx: ( Math.random() - 0.5 ) * 0.4,
				o: Math.random() * 0.35 + 0.2,
				c: CONFETTI_COLORS[ Math.floor( Math.random() * CONFETTI_COLORS.length ) ],
			} );
		}
		for ( let i = particles.length - 1; i >= 0; i-- ) {
			const p = particles[i];
			p.x += p.vx;
			p.y += p.vy;
			ctx.globalAlpha = p.o;
			ctx.fillStyle = p.c;
			ctx.fillRect( p.x, p.y, p.s, p.s );
			if ( p.y > canvas.height ) particles.splice( i, 1 );
		}
		ctx.globalAlpha = 1;
		animId = requestAnimationFrame( draw );
	};
	draw();
	confettiCanvases.set( container, { canvas, animId } );
}

function burstConfetti( container ) {
	const existing = confettiCanvases.get( container );
	if ( existing ) {
		cancelAnimationFrame( existing.animId );
		if ( existing.canvas.parentNode ) existing.canvas.parentNode.removeChild( existing.canvas );
		confettiCanvases.delete( container );
	}

	const canvas = document.createElement( 'canvas' );
	canvas.className = 'drill-nav__confetti';
	container.style.position = 'relative';
	container.appendChild( canvas );

	const ctx = canvas.getContext( '2d' );
	canvas.width = container.offsetWidth;
	canvas.height = container.offsetHeight;

	const particles = [];
	const cx = canvas.width / 2;
	const cy = canvas.height / 2;

	for ( let i = 0; i < 100; i++ ) {
		const angle = Math.random() * Math.PI * 2;
		const speed = Math.random() * 7 + 2;
		particles.push( {
			x: cx, y: cy,
			vx: Math.cos( angle ) * speed,
			vy: Math.sin( angle ) * speed,
			s: Math.random() * 5 + 2,
			c: CONFETTI_COLORS[ Math.floor( Math.random() * CONFETTI_COLORS.length ) ],
			o: 1,
			decay: Math.random() * 0.012 + 0.008,
			g: 0.07,
		} );
	}

	const draw = () => {
		ctx.clearRect( 0, 0, canvas.width, canvas.height );
		for ( let i = particles.length - 1; i >= 0; i-- ) {
			const p = particles[i];
			p.x += p.vx;
			p.y += p.vy;
			p.vy += p.g;
			p.o -= p.decay;
			if ( p.o <= 0 ) { particles.splice( i, 1 ); continue; }
			ctx.globalAlpha = p.o;
			ctx.fillStyle = p.c;
			ctx.beginPath();
			ctx.arc( p.x, p.y, p.s, 0, Math.PI * 2 );
			ctx.fill();
		}
		ctx.globalAlpha = 1;
		if ( particles.length > 0 ) {
			requestAnimationFrame( draw );
		} else if ( canvas.parentNode ) {
			canvas.parentNode.removeChild( canvas );
		}
	};
	draw();
}

// ---------------------------------------------------------------------------
// Language selector — custom dropdown triggering Google Translate
// ---------------------------------------------------------------------------
function initTranslateDropdown() {
	const wrappers = document.querySelectorAll( '[data-translate-toggle]' );
	if ( !wrappers.length ) return;

	wrappers.forEach( ( wrapper ) => {
		const btn = wrapper.querySelector( 'button[aria-haspopup]' );
		const links = wrapper.querySelectorAll( '[data-lang]' );

		if ( !btn ) return;

		// Toggle dropdown.
		btn.addEventListener( 'click', () => {
			const expanded = btn.getAttribute( 'aria-expanded' ) === 'true';
			// Close all other translate dropdowns first.
			wrappers.forEach( ( w ) => {
				const b = w.querySelector( 'button[aria-haspopup]' );
				if ( b && b !== btn ) b.setAttribute( 'aria-expanded', 'false' );
			} );
			btn.setAttribute( 'aria-expanded', expanded ? 'false' : 'true' );
		} );

		// Close on outside click.
		document.addEventListener( 'click', ( e ) => {
			if ( !wrapper.contains( e.target ) ) {
				btn.setAttribute( 'aria-expanded', 'false' );
			}
		} );

		// Language link clicks — trigger Google Translate.
		// Weglot-served languages are tagged with [data-wg-notranslate] and
		// have a real href (e.g. /fr/, /es/) — let the browser navigate to
		// them naturally so the Weglot plugin can intercept server-side.
		links.forEach( ( link ) => {
			if ( link.hasAttribute( 'data-wg-notranslate' ) ) {
				return;
			}
			link.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				const lang = link.getAttribute( 'data-lang' );

				if ( lang === 'en' ) {
					document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
					document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.' + window.location.hostname;
					window.location.reload();
					return;
				}

				document.cookie = 'googtrans=/en/' + lang + '; path=/;';
				document.cookie = 'googtrans=/en/' + lang + '; path=/; domain=.' + window.location.hostname;

				const combo = document.querySelector( '.goog-te-combo' );
				if ( combo ) {
					combo.value = lang;
					combo.dispatchEvent( new Event( 'change' ) );
				} else {
					window.location.reload();
				}

				// Update all translate buttons with the selected language.
				wrappers.forEach( ( w ) => {
					const b = w.querySelector( 'button[aria-haspopup] span' );
					if ( b ) b.textContent = link.textContent;
				} );
				btn.setAttribute( 'aria-expanded', 'false' );
			} );
		} );
	} );
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------
function escapeHtml( str ) {
	const div = document.createElement( 'div' );
	div.textContent = str;
	return div.innerHTML;
}

function escapeAttr( str ) {
	return str.replace( /&/g, '&amp;' ).replace( /"/g, '&quot;' ).replace( /'/g, '&#39;' ).replace( /</g, '&lt;' );
}
